import { FC, useCallback } from 'react'
import { Button } from '@/atoms/Button'
import { CalendarIcon } from '@/atoms/Icons/CalendarIcon'
import { CopyIcon } from '@/atoms/Icons/CopyIcon'
import { SceneIcon } from '@/atoms/Icons/SceneIcon'
import { HeadingMD, HeadingXS, LabelSM, ParagraphSM } from '@/atoms/Text'
import { paths } from '@/constants'
import { useToast } from '@/molecules/Toast'
import { useCollectiblesPlayerEngagement } from '@/organisms/CollectiblesPlayer/CollectiblesPlayerEngagement/CollectiblesPlayerEngagementContext'
import { CollectibleGift } from '@/types/codegen-federation'
import { useLocale } from '@/utils/LocaleUtil'
import { useSafeTrack } from '@/utils/analytics'
import { useTranslate } from '@/utils/translate/translate-client'

interface Props {
  gift: CollectibleGift
  frame: string
}

export const CollectibleGiftMessage: FC<Props> = ({ gift, frame }) => {
  const { t } = useTranslate('watch')
  const toast = useToast()
  const track = useSafeTrack()

  const { engagementMoment } = useCollectiblesPlayerEngagement()

  const { createdAt, message, toFirstName, toLastName } = gift
  const { locale } = useLocale()

  const shareUrl = `${paths.getBaseURL()}${paths.share.moment}/${engagementMoment?.id}/frame/${frame}`
  const linkCopied = t('linkCopied', 'Link copied.')

  const date = new Date(createdAt).toLocaleDateString(locale, {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  })

  const onShareClick = useCallback(() => {
    track('Frame Share Clicked', {
      type: 'Copy to Clipboard',
      momentId: engagementMoment?.id,
      momentTitle: engagementMoment?.title,
      projectSlug: engagementMoment?.projectSlug,
      episodeGuid: engagementMoment?.episode?.guid,
      frameId: frame,
    })
    navigator.clipboard.writeText(shareUrl as string)
    toast?.showToast(linkCopied, { icon: <CopyIcon size={24} color="gray-200" /> })
  }, [engagementMoment, frame, linkCopied, shareUrl, toast, track])

  let formattedMessage = null
  if (message) {
    try {
      formattedMessage = JSON.parse(message)
    } catch (error) {
      formattedMessage = [{ message, name: gift.gifter?.firstName }]
    }
  }

  return (
    <div>
      <div className="relative mx-auto mb-6 h-[450px] max-w-[340px] rounded-xl bg-core-gray-100 p-4 xl:mx-4 xl:max-w-[390px]">
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
          <div className="clip-path-notched relative h-[421px] w-[312px] overflow-hidden bg-gray-300 xl:w-[371px]" />
        </div>
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
          <div className="clip-path-notched relative h-[419px] w-[310px] overflow-hidden bg-gray-100 xl:w-[369px]" />
        </div>
        <div className="absolute left-1/2 top-1/2 flex h-[405px] w-[295px] -translate-x-1/2 -translate-y-1/2 flex-col justify-center border border-gray-300 px-7 xl:w-[350px]">
          <div className="mx-auto mt-1 flex w-fit rounded-2xl bg-core-gray-200 px-3 py-1 text-core-gray-600">
            <CalendarIcon size={16} color="core-gray-600" className="mr-1" />
            <LabelSM>{date}</LabelSM>
          </div>
          <HeadingMD color="core-gray-900" className="photon-heading-sm lg:photon-heading-md my-3 text-center">
            <span className="photon-title-sm uppercase">{t('to', 'To')}</span> {toFirstName} {toLastName}
          </HeadingMD>
          {gift.gifter && gift.gifter.firstName && gift.gifter.lastName && (
            <HeadingXS color="core-gray-700" className="photon-paragraph-xl lg:photon-heading-xs mb-4 text-center">
              <span className="photon-title-sm uppercase">{t('from', 'From')}</span> {gift.gifter?.firstName}{' '}
              {gift.gifter?.lastName}
            </HeadingXS>
          )}
          <div className="mx-auto mb-6 w-fit">
            <svg xmlns="http://www.w3.org/2000/svg" width="150" height="2" viewBox="0 0 150 2" fill="none">
              <path d="M1 1H149" stroke="#DFDEDD" stroke-linecap="round" />
            </svg>
          </div>
          <ParagraphSM color="core-gray-800">{toFirstName},</ParagraphSM>
          <div className="my-3 h-20 overflow-y-scroll scrollbar-none lg:h-40">
            {formattedMessage?.map((m: { message: string; name: string }, index: number) => (
              <div key={`${m.message}+${index}`}>
                <ParagraphSM color="core-gray-800" className="mb-1">
                  {m.message}
                </ParagraphSM>
                <ParagraphSM color="core-gray-800">- {m.name}</ParagraphSM>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="m-3 mx-auto max-w-[340px]">
        <Button variant="white" outline onClick={onShareClick} className="w-full">
          <SceneIcon size={24} color2="core-gray-800" className="mr-2" />
          {t('shareFrame', 'Share Frame')}
        </Button>
      </div>
    </div>
  )
}
